import React, { useEffect } from 'react';
import Grid from '@mui/material/Grid';
import { UseFormReturn } from 'react-hook-form';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTranslation } from 'react-i18next';

import { FormTitle, FormItem } from 'components/shared';
import { useSubForm } from 'hooks/useSubForm';
import {
  AutocompleteElement,
  TextFieldElement
} from 'components/muiInputWrappers';
import { KEYS } from 'helpers/react-query.config';
import { useGlobalContext } from 'context/GlobalContext';
import { useApiContext } from 'context/ApiContext';
import { ShipmentResponse } from 'api/inventory';

dayjs.extend(utc);
dayjs.extend(timezone);

export type FormFields = {
  courierId: number;
  trackingNumber: number;
  estimatedArrival: Date;
};

type Props = {
  shipment?: ShipmentResponse;
  form: UseFormReturn<any, any>;
  name: string;
  readOnly?: boolean;
  fullWidth?: boolean;
};

function ShippingInformation({
  shipment,
  form,
  name,
  readOnly = false,
  fullWidth = false
}: Props) {
  const { control, getName } = useSubForm<FormFields>({
    form,
    name
  });

  const { showApiError } = useGlobalContext();
  const { catalog: catalogApi } = useApiContext();
  const { t } = useTranslation('global');

  const { data: courierOptions } = useQuery(
    [KEYS.catalog.inventory.getCouriers()],
    () => catalogApi.inventory.getCouriers(),
    {
      select: (data) =>
        data.data.data.map((opt) => ({
          id: opt.id,
          label: opt.description
        })),
      onError: (error) => showApiError(error, t('errorMessages.couriers')),
      refetchOnWindowFocus: false
    }
  );

  useEffect(() => {
    if (shipment) {
      form.setValue(getName('courierId'), shipment.courierId);
      form.setValue(getName('trackingNumber'), shipment.guideNumber);
      form.setValue(
        getName('estimatedArrival'),
        dayjs(`${shipment.shippingDate}.000+00:00`)
          .tz(dayjs.tz.guess())
          .format('YYYY-MM-DD')
      );
    }
  }, [shipment]);

  return (
    <Grid spacing={2} container>
      <Grid
        lg={fullWidth ? 12 : 4}
        md={fullWidth ? 12 : 4}
        sm={12}
        xs={12}
        item
      >
        <FormTitle variant="body2" gutterBottom>
          Mensajería
        </FormTitle>
        <FormItem>
          <AutocompleteElement
            autocompleteProps={{
              size: 'small',
              disabled: readOnly
            }}
            control={form.control}
            loading={false}
            name={getName('courierId')}
            options={courierOptions || []}
            matchId
            required
          />
        </FormItem>
      </Grid>
      <Grid
        lg={fullWidth ? 12 : 4}
        md={fullWidth ? 12 : 4}
        sm={12}
        xs={12}
        item
      >
        <FormTitle variant="body2" gutterBottom>
          N° de guía
        </FormTitle>
        <FormItem>
          <TextFieldElement
            control={control}
            disabled={readOnly}
            name={getName('trackingNumber')}
            size="small"
            variant="outlined"
            fullWidth
            required
          />
        </FormItem>
      </Grid>
      <Grid
        lg={fullWidth ? 12 : 4}
        md={fullWidth ? 12 : 4}
        sm={12}
        xs={12}
        item
      >
        <FormTitle variant="body2" gutterBottom>
          Fecha estimada de entrega
        </FormTitle>
        <FormItem>
          <TextFieldElement
            control={control}
            disabled={readOnly}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: dayjs().toISOString().slice(0, 10),
              max: '9999-12-31'
            }}
            name={getName('estimatedArrival')}
            size="small"
            type="date"
            variant="outlined"
            fullWidth
            required
          />
        </FormItem>
      </Grid>
    </Grid>
  );
}

export default ShippingInformation;
